import React, { useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';

interface CausalNode {
  id: string;
  label?: string;
  type?: string;
  scene_id?: string;
  episode?: string;
  device_type?: string;
  [key: string]: any;
}

interface CausalEdge {
  source: string;
  target: string;
  label?: string;
  relation?: string;
  [key: string]: any;
}

interface CausalGraphPanelProps {
  graph?: {
    nodes?: Record<string, CausalNode>;
    edges?: CausalEdge[];
    node_count?: number;
    edge_count?: number;
  } | null;
  locale?: 'zh' | 'en';
}

const LABELS = {
  zh: {
    title: '因果剧情图谱',
    noData: '暂无因果剧情图数据。生成结构后将自动构建。',
    nodes: '节点',
    edges: '边',
    setup: '铺垫',
    payoff: '兑现',
    cause: '因果',
    conflict: '冲突',
    scene: '场景',
    device: '装置',
  },
  en: {
    title: 'Causal Plot Graph',
    noData: 'No causal graph data yet. Will auto-build after structure generation.',
    nodes: 'Nodes',
    edges: 'Edges',
    setup: 'Setup',
    payoff: 'Payoff',
    cause: 'Cause',
    conflict: 'Conflict',
    scene: 'Scene',
    device: 'Device',
  },
};

const nodeTypeColors: Record<string, string> = {
  scene: '#3b82f6',
  device: '#f59e0b',
  character: '#10b981',
  event: '#8b5cf6',
  hook: '#ef4444',
  payoff: '#ec4899',
  cliffhanger: '#6366f1',
  foreshadowing: '#14b8a6',
  default: '#6b7280',
};

function getNodeColor(nodeType?: string): string {
  if (!nodeType) return nodeTypeColors.default;
  const key = Object.keys(nodeTypeColors).find(k => nodeType.toLowerCase().includes(k));
  return key ? nodeTypeColors[key] : nodeTypeColors.default;
}

const CustomNode: React.FC<NodeProps<CausalNode>> = ({ data }) => {
  const color = getNodeColor(data?.type);
  return (
    <div
      style={{
        padding: '8px 12px',
        borderRadius: '6px',
        background: '#fff',
        border: `2px solid ${color}`,
        fontSize: '12px',
        minWidth: '100px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: color }} />
      <div style={{ fontWeight: 600, color }}>{data?.label || data?.id}</div>
      {data?.episode && <div style={{ fontSize: '10px', color: '#888' }}>{data.episode}</div>}
      {data?.device_type && <div style={{ fontSize: '10px', color: '#666' }}>{data.device_type}</div>}
      <Handle type="source" position={Position.Bottom} style={{ background: color }} />
    </div>
  );
};

const nodeTypes = { custom: CustomNode };

const CausalGraphPanel: React.FC<CausalGraphPanelProps> = ({ graph, locale = 'zh' }) => {
  const t = LABELS[locale];

  if (!graph || (!graph.nodes && !graph.edges)) {
    return (
      <div className="panel-card">
        <div className="panel-card__header">
          <div className="panel-title">
            <span className="panel-title__icon">🕸️</span>
            <h2>{t.title}</h2>
          </div>
        </div>
        <p className="empty-copy">{t.noData}</p>
      </div>
    );
  }

  const rawNodes = graph.nodes || {};
  const rawEdges = graph.edges || [];

  const initialNodes = Object.entries(rawNodes).map(([id, node], index) => ({
    id,
    type: 'custom',
    position: { x: (index % 5) * 180 + 50, y: Math.floor(index / 5) * 120 + 50 },
    data: { ...node, id },
  }));

  const initialEdges = rawEdges.map((edge, index) => ({
    id: `e${index}`,
    source: edge.source,
    target: edge.target,
    label: edge.label || edge.relation || '',
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#94a3b8', strokeWidth: 1.5 },
    labelStyle: { fontSize: 10, fill: '#64748b' },
  }));

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  const onInit = useCallback((reactFlowInstance: any) => {
    setTimeout(() => reactFlowInstance.fitView({ padding: 0.2 }), 100);
  }, []);

  return (
    <div className="panel-card">
      <div className="panel-card__header">
        <div className="panel-title">
          <span className="panel-title__icon">🕸️</span>
          <h2>{t.title}</h2>
        </div>
        <div className="impact-metrics">
          <span className="app-pill">
            <span>🟢</span>
            <span>{t.nodes}: {graph.node_count ?? initialNodes.length}</span>
          </span>
          <span className="app-pill">
            <span>↔️</span>
            <span>{t.edges}: {graph.edge_count ?? initialEdges.length}</span>
          </span>
        </div>
      </div>
      <div style={{ height: '400px', width: '100%' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onInit={onInit}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-right"
        >
          <Background gap={16} size={1} color="#e2e8f0" />
          <MiniMap nodeStrokeWidth={3} zoomable pannable />
          <Controls />
        </ReactFlow>
      </div>
    </div>
  );
};

export default CausalGraphPanel;
